// AuthContext.js - Updated with URL token handling
import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
} from "react";
import axios from "axios";
import Cookies from "js-cookie";
import { API_URL } from "../config";
import { toast } from "react-toastify";

// Add a custom event for auth status changes
const AUTH_STATUS_CHANGED = "auth_status_changed";

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === null) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [logoutLoading, setLogoutLoading] = useState(false);
  const [accessToken, setAccessToken] = useState(
    () => Cookies.get("access_token") || null
  );

  // Handle OAuth redirect and extract tokens from URL

  const fetchUserData = useCallback(
    async (forceRefresh = false) => {
      try {
        setLoading(true);

        // Get token from state or cookies
        const token = accessToken || Cookies.get("access_token");
        if (!token) {
          setLoading(false);
          return null;
        }

        // Only set state if token changed
        if (token !== accessToken) {
          setAccessToken(token);
        }

        const response = await axios.get(`${API_URL}/auth/me`, {
          withCredentials: true,
          headers: {
            Authorization: token,
            "Cache-Control": forceRefresh ? "no-cache" : undefined,
          },
        });

        if (response.data) {
          const userData = {
            id: response.data.id,
            email: response.data.email || "",
            name: response.data.full_name || "",
            picture: response.data.avatar_url || "/default-avatar.svg",
            primaryAccountId: response.data.primary_account_id,
            accounts: response.data.accounts || [],
          };

          setUser(userData);

          localStorage.setItem("user_id", userData.id);
          localStorage.setItem("user_email", userData.email);
          localStorage.setItem("user_name", userData.name);
          localStorage.setItem(
            "user_picture",
            userData.picture || "/default-avatar.svg"
          );

          // Store primary account ID if available
          if (userData.primaryAccountId) {
            localStorage.setItem(
              "primary_account_id",
              userData.primaryAccountId
            );
          }

          // Store first account ID as current if no primary is set
          if (userData.accounts && userData.accounts.length > 0) {
            const defaultAccount = userData.accounts[0];
            localStorage.setItem(
              "current_account_id",
              userData.primaryAccountId || defaultAccount.id
            );

            // Also store the account email for reference
            localStorage.setItem("current_account_email", defaultAccount.email);
            localStorage.setItem(
              "current_account_provider",
              defaultAccount.provider
            );
          }

          // Dispatch event to notify components of auth change
          window.dispatchEvent(new CustomEvent(AUTH_STATUS_CHANGED));

          setLoading(false);
          return userData;
        }

        setLoading(false);
        return null;
      } catch (error) {
        console.error("Error fetching user data:", error);
        setLoading(false);
        return null;
      }
    },
    [accessToken]
  );

  const logout = useCallback(async () => {
    try {
      setLogoutLoading(true);
      const refreshToken = Cookies.get("refresh_token");

      // Only make the API call if we have a refresh token
      if (refreshToken) {
        try {
          await axios.post(
            `${API_URL}/auth/logout`,
            { refresh_token: refreshToken },
            {
              withCredentials: true,
              headers: {
                Authorization: accessToken,
              },
            }
          );
          console.log("Successfully logged out on server");
        } catch (apiError) {
          console.error("Error calling logout API:", apiError);
          // Continue with client-side logout even if API fails
        }
      } else {
        console.warn("No refresh token found for logout API call");
      }

      // Clear all cookies
      const allCookies = Object.keys(Cookies.get());
      allCookies.forEach((cookieName) => {
        Cookies.remove(cookieName);
      });

      // Clear localStorage
      localStorage.clear();

      // Clear sessionStorage
      sessionStorage.clear();

      // Clear specific auth-related cookies (in case the above missed any due to path/domain issues)
      Cookies.remove("access_token", { path: "/" });
      Cookies.remove("refresh_token", { path: "/" });
      Cookies.remove("account_id", { path: "/" });
      Cookies.remove("user_id", { path: "/" });
      Cookies.remove("google_token", { path: "/" });
      Cookies.remove("outlook_token", { path: "/" });
      Cookies.remove("provider", { path: "/" });

      // Reset state
      setUser(null);
      setAccessToken(null);

      // Dispatch event to notify components of auth change
      window.dispatchEvent(new CustomEvent(AUTH_STATUS_CHANGED));

      console.log("Local logout completed successfully");
      toast.success("Logged out successfully");

      return true;
    } catch (error) {
      console.error("Logout error:", error);

      // Attempt to clear everything even if there was an error
      try {
        // Clear localStorage
        localStorage.clear();

        // Clear sessionStorage
        sessionStorage.clear();

        // Clear all cookies
        const allCookies = Object.keys(Cookies.get());
        allCookies.forEach((cookieName) => {
          Cookies.remove(cookieName);
          Cookies.remove(cookieName, { path: "/" });
        });

        setUser(null);
        setAccessToken(null);
      } catch (clearError) {
        console.error(
          "Error while clearing storage during logout:",
          clearError
        );
      }

      return true;
    } finally {
      // Always set logoutLoading to false when logout completes, regardless of success or failure
      setLogoutLoading(false);
    }
  }, [accessToken]);

  // Check token and fetch user data on initial load
  const token = Cookies.get("access_token");
  useEffect(() => {
    if (token) {
      setAccessToken(token);
      fetchUserData();
    } else {
      setLoading(false);
    }
  }, [fetchUserData, token]);

  // Setup axios interceptor for authorization
  useEffect(() => {
    const interceptor = axios.interceptors.request.use(
      (config) => {
        const token = Cookies.get("access_token") || accessToken;
        if (token) {
          // Ensure token has Bearer prefix
          const authToken = token.startsWith("Bearer ")
            ? token
            : `Bearer ${token}`;
          config.headers.Authorization = authToken;
        }
        // Add withCredentials to ensure cookies are sent with cross-origin requests
        config.withCredentials = true;
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    // Clean up interceptor on unmount
    return () => {
      axios.interceptors.request.eject(interceptor);
    };
  }, [accessToken]);

  const value = {
    user,
    loading,
    logoutLoading,
    accessToken,
    fetchUserData,
    logout,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
